<template>
  <div class="fc-cxc">
    <!-- ───────── HERO ───────── -->
    <header class="fc-hero">
      <div class="fc-hero__left">
        <span class="fc-hero__eyebrow">
          <v-icon size="16" color="primary">mdi-cash-multiple</v-icon>
          Módulo · Cobranzas
        </span>
        <h1 class="fc-hero__title">Cuentas por cobrar</h1>
        <p class="fc-hero__lede">
          Saldos de clientes, registro de cobros y antigüedad de cartera para
          <strong>{{ currentBusinessUnitName }}</strong>.
        </p>
      </div>

      <div class="fc-hero__right">
        <div class="fc-hero__date">
          <span class="fc-hero__date-day">{{ today.day }}</span>
          <span class="fc-hero__date-rest">
            <span class="fc-hero__date-month">{{ today.month }}</span>
            <span class="fc-hero__date-year">{{ today.year }}</span>
          </span>
        </div>
        <span class="fc-chip-live">
          <span class="fc-chip-live__dot"></span>
          Datos en vivo
        </span>
      </div>
    </header>

    <!-- ───────── TABS ───────── -->
    <div class="fc-card fc-card--board">
      <v-tabs v-model="typeForm" background-color="transparent" color="primary" slider-color="primary" slider-size="3"
        grow class="fc-tabs">
        <v-tab class="fc-tab">
          <v-icon left size="18">mdi-account-multiple</v-icon>
          Clientes
        </v-tab>
        <v-tab class="fc-tab">
          <v-icon left size="18">mdi-cash-register</v-icon>
          Listado de cobros
        </v-tab>
        <v-tab class="fc-tab">
          <v-icon left size="18">mdi-file-chart</v-icon>
          Reportes
        </v-tab>
      </v-tabs>
    </div>

    <!-- ───────── FILTROS ───────── -->
    <section class="fc-section">
      <div class="fc-section__head">
        <v-icon size="20" color="primary">mdi-filter-variant</v-icon>
        <h2 class="fc-section__title">Filtros</h2>
        <v-progress-circular v-if="loading" indeterminate size="16" width="2" color="primary" class="ml-2" />
        <span v-if="loading" class="fc-section__status">Cargando…</span>
      </div>
      <div class="fc-card fc-card--filters">
        <v-row align="center" class="ma-0">
          <!-- UNIDAD DE NEGOCIOS -->
          <v-col cols="12" sm="auto" md="auto" lg="auto" class="py-2">
            <v-autocomplete v-model="business_selected" :items="itemsBussines" label="Unidad de negocio" color="primary"
              item-color="primary" prepend-inner-icon="mdi-office-building-outline" outlined dense
              @change="updateLocationsForBusiness()" hide-details>
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-office-building-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="auto" md="auto" lg="auto" class="py-2">
            <v-autocomplete v-model="location_selected" :items="itemsLocations2" return-object color="primary"
              item-color="primary" label="Sucursal" outlined dense @change="update_filters()" hide-details>
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-map-marker-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" class="pl-2" md="auto" v-if="useRangeFilter">
            <v-menu v-model="menu_date_start" :close-on-content-click="false" transition="slide-y-transition" offset-y
              min-width="auto" max-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="dateStartFormatDMY" label="Fecha inicio" readonly v-bind="attrs" v-on="on"
                  outlined dense hide-details color="primary">
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-calendar-start</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker v-model="dateStart" @input="menu_date_start = false, update_filters()" color="primary"
                header-color="primary" show-current locale="es"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" class="pl-2" md="auto">
            <v-menu v-model="menu_date_end" :close-on-content-click="false" transition="slide-y-transition" offset-y
              min-width="auto" max-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="date2FormatDMY" label="Fecha fin" readonly v-bind="attrs" v-on="on" outlined
                  dense hide-details color="primary">
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-calendar-end</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker v-model="date2" @input="menu_date_end = false, update_filters()" color="primary"
                header-color="primary" show-current locale="es"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" class="pl-2" md="auto">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :color="useRangeFilter ? 'primary' : 'grey'" @click="toggleRangeFilter" v-bind="attrs"
                  v-on="on">
                  <v-icon>{{ useRangeFilter ? 'mdi-calendar-range' : 'mdi-calendar' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ useRangeFilter ? 'Usar solo fecha fin' : 'Usar rango de fechas' }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" class="pl-2" md="auto" v-if="typeForm === 0">
            <v-select v-model="aging_filter" :items="aging_options" label="Antigüedad" outlined dense hide-details
              clearable @change="update_filters()" color="primary">
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-clock-alert-outline</v-icon>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </div>
    </section>

    <!-- ───────── CONTENIDO ───────── -->
    <v-tabs-items v-model="typeForm" class="fc-panes">
      <!-- ════ TAB · CLIENTES ════ -->
      <v-tab-item>
        <section v-if="hasData" class="fc-section">
          <div class="fc-section__head">
            <v-icon size="20" color="primary">mdi-chart-box-outline</v-icon>
            <h2 class="fc-section__title">Resumen de cartera</h2>
          </div>
          <div class="fc-kpis">
            <article
              v-for="(stat, index) in consolidatedStats"
              :key="index"
              class="fc-kpi"
              :class="`fc-kpi--${stat.tone}`"
            >
              <div class="fc-kpi__top">
                <span class="fc-kpi__icon">
                  <v-icon size="22" :color="stat.color">{{ stat.icon }}</v-icon>
                </span>
              </div>
              <span class="fc-kpi__label">{{ stat.title }}</span>
              <div class="fc-kpi__values">
                <template v-if="stat.values && stat.values.length">
                  <div v-for="(value, idx) in stat.values" :key="idx" class="fc-kpi__money">
                    <span class="fc-kpi__number">{{ currencyFormat(value.amount) }}</span>
                    <span class="fc-kpi__currency">{{ value.currencyCode }}</span>
                  </div>
                </template>
                <div v-else class="fc-kpi__money">
                  <span class="fc-kpi__number">{{ currencyFormat(0) }}</span>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section class="fc-section">
          <div class="fc-card fc-card--table">
            <div class="fc-table__head">
              <span class="fc-table__title">
                <v-icon color="primary" size="20" class="mr-2">mdi-account-multiple</v-icon>
                Clientes
              </span>
              <v-spacer></v-spacer>
              <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar" single-line hide-details dense
                outlined class="fc-search" style="max-width: 280px"></v-text-field>
            </div>

            <v-data-table :headers="headers" :items="clients" :search="search" :loading="loading" :items-per-page="10"
              class="fc-data-table elevation-0">
                <template v-slot:item.entity_name="{ item }">
                  <v-hover v-slot="{ hover }">
                    <span @click="viewEntity(item.entity_id, true)" class="primary--text font-weight-medium"
                      style="cursor: pointer" :class="{ 'red--text': hover, 'text-decoration-underline': hover }">
                      {{ item.entity_name }}
                    </span>
                  </v-hover>
                </template>

                <template v-slot:item.total_pending_amount="{ item }">
                  <div class="error--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.pending_amount) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>
                <template v-slot:item.total_debit_amount="{ item }">
                  <div class="warning--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.total_debits) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>

                <template v-slot:item.total_credit_amount="{ item }">
                  <div class="warning--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.total_credits) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>

                <template v-slot:item.total_paid_amount="{ item }">
                  <div class="success--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.total_paid) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip top v-if="hasFeature('CXC-012')">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" :loading="loadingInvoices" v-bind="attrs" v-on="on"
                        @click="viewInvoices(item)">
                        <v-icon>mdi-cash-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Agregar cobro</span>
                  </v-tooltip>
                </template>

                <template v-slot:no-data>
                  <v-row justify="center" align="center" class="py-10">
                    <v-icon size="64" color="grey lighten-1">mdi-database-off</v-icon>
                  </v-row>
                  <v-row justify="center" class="pb-6">
                    <span class="text-subtitle-1 grey--text">No hay datos disponibles</span>
                  </v-row>
                </template>
            </v-data-table>
          </div>
        </section>
      </v-tab-item>

      <!-- ════ TAB · LISTADO DE COBROS ════ -->
      <v-tab-item>
        <section class="fc-section">
          <div class="fc-card fc-card--table">
            <div class="fc-table__head">
              <span class="fc-table__title">
                <v-icon color="primary" size="20" class="mr-2">mdi-cash-register</v-icon>
                Pagos
              </span>
              <v-spacer></v-spacer>
              <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar" single-line hide-details dense
                outlined class="fc-search mr-3" style="max-width: 280px"></v-text-field>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="grey darken-1" outlined small v-bind="attrs" v-on="on" class="mr-2 text-none">
                      <v-icon left small>mdi-download</v-icon>
                      Exportar
                      <v-icon right x-small>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="export_payment_details()">
                      <v-list-item-icon>
                        <v-icon color="success">mdi-file-excel</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Detallado</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">Por factura</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
            </div>

            <v-data-table :headers="headersTransactions" :items="transactions" :search="search" :loading="loading"
              :items-per-page="transactionsLimit" hide-default-footer class="fc-data-table elevation-0">
                <template v-slot:[`item.invoice_payment_id`]="{ item }">
                  <v-hover v-slot="{ hover }">
                    <span @click="showPayments(item.invoice_payment_id, 1)"
                      class="primary--text font-weight-normal text-truncate font-weight-semibold" :style="{
                        color: hover ? 'red' : 'black',
                        cursor: hover ? 'pointer' : '',
                        'text-decoration': hover ? 'underline' : '',
                      }">
                      {{ filLeft(item.invoice_payment_id.toString(), 12, "0") }}
                    </span>
                  </v-hover>
                </template>
                <template v-slot:item.total_amount="{ item }">
                  <span class="font-weight-medium">
                    {{ currencyFormat(item.total_amount) }}
                    {{ item.currency_code }}
                  </span>
                </template>

                <template v-slot:item.status_id="{ item }">
                  <v-chip small :color="item.status_id != 4 ? 'success' : 'error'" text-color="white">
                    {{ item.status_id != 4 ? item.status_name : "Anulado" }}
                  </v-chip>
                </template>

                <template v-slot:item.accounting="{ item }">
                  <v-chip small :color="item.recorded ? 'success' : 'warning'" text-color="white">
                    {{ item.recorded ? "Contabilizado" : "Sin contabilizar" }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip top v-if="item.status_id == 1 && hasFeature('CXC-012')">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="openCancelPaymentDialog(item)">
                        <v-icon>mdi-cancel</v-icon>
                      </v-btn>
                    </template>
                    <span>Anular pago</span>
                  </v-tooltip>
                </template>

                <template v-slot:no-data>
                  <v-row justify="center" align="center" class="py-10">
                    <v-icon size="64" color="grey lighten-1">mdi-database-off</v-icon>
                  </v-row>
                  <v-row justify="center" class="pb-6">
                    <span class="text-subtitle-1 grey--text">No hay datos disponibles</span>
                  </v-row>
                </template>
                
                <template slot="footer">
                  <v-divider></v-divider>
                  <div class="pa-4">
                    <v-row justify="center" align="center">
                      <v-col cols="auto" class="text-caption grey--text">
                        Mostrando {{ (transactionsPage - 1) * transactionsLimit + 1 }} - {{ Math.min(transactionsPage * transactionsLimit, transactionsTotal) }} de {{ transactionsTotal }} pagos
                      </v-col>
                    </v-row>
                    <v-row justify="center" align="center">
                      <v-col cols="auto">
                        <v-pagination 
                          v-model="transactionsPage" 
                          :length="transactionsTotalPages" 
                          :total-visible="7" 
                          color="primary"
                          circle 
                          :disabled="loading" 
                          @input="onTransactionsPageChange">
                        </v-pagination>
                      </v-col>
                    </v-row>
                  </div>
                </template>
              </v-data-table>
          </div>
        </section>
      </v-tab-item>

      <!-- ════ TAB · REPORTES ════ -->
      <v-tab-item>
        <section class="fc-section">
          <div class="fc-card fc-card--board">
            <v-tabs v-model="reportSubTab" color="primary" slider-color="primary" slider-size="3"
              background-color="transparent" class="fc-tabs">
              <v-tab class="fc-tab"><v-icon left size="18">mdi-chart-bar</v-icon>Dashboard</v-tab>
              <v-tab class="fc-tab"><v-icon left size="18">mdi-microsoft-excel</v-icon>Excel / Reporte</v-tab>
            </v-tabs>
            <v-divider></v-divider>
            <div class="fc-board__pane">
              <v-tabs-items v-model="reportSubTab" style="background:transparent;">
              <v-tab-item>
                <AgingReportDashboard
                  :data="dashboardData"
                  :loading="dashboardLoading"
                  moduleType="cxc"
                  :businessUnitName="currentBusinessUnitName"
                  :locationName="currentLocationName"
                  :currencyName="currentCurrencyCode"
                  :reportDate="date2"
                  :currencies="currencies"
                  :allCurrencies="allSystemCurrencies"
                  :filters="dashboardFilters"
                  :businessId="lastData ? lastData.business : 0"
                  @currency-change="onDashboardCurrencyChange"
                  @convert-currency-change="onDashboardConvertChange"
                />
              </v-tab-item>
              <v-tab-item>
                <AgingReportsvue :aging_type="2" />
              </v-tab-item>
              </v-tabs-items>
            </div>
          </div>
        </section>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
      <v-card flat class="mx-2 my-0 py-0">
        <v-card-text class="pa-2">
          <v-row justify="center">
            <v-col cols="12" class="pa-0">
              <viewInvoices class="m" v-if="!loaded" @close="reset()" :type="true" :title="'Cuentas por cobrar'"
                :data_invoices="selectedClients" @refreshInvoices="refreshInvoices()" :filters="filters" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCancelPayment" max-width="400">
      <v-card>
        <v-card-title class="headline primary white--text">Anular cobro</v-card-title>
        <v-card-text class="pt-4">
          <p class="text-h6 text-center">¿Seguro que deseas anular este cobro?</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn text @click="dialogCancelPayment = false">Cancelar</v-btn>
          <v-btn color="error" outlined @click="cancelPayment">Anular cobro</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import viewInvoices from "@/components/Transactions/ViewInvoicesCxC.vue";
import AgingReportDashboard from "@/components/Transactions/AgingReportDashboard.vue";
import AgingReportsvue from "@/components/Accounting/AgingReportsvue.vue";
import moment from "moment";
moment.locale("es");
export default {
  components: { viewInvoices, AgingReportDashboard, AgingReportsvue },
  data: () => ({
    loading: false,
    dialogCancelPayment: false,
    filters: {},
    lastFilters: {},
    lastPaymentsFilter: '',
    loading_export: false,
    typeForm: 0,
    reportSubTab: 0,
    loaded: true,
    dialog: false,
    loadingInvoices: false,
    search: "",
    clients: [],
    hasData: false,
    headers: [
      { text: "Cliente", value: "entity_name", align: "start" },
      // { text: "Cant facturas", value: "invoice_quantity", align: "end" },
      { text: "Notas de debito", value: "total_debit_amount", align: "end" },
      { text: "Notas de crédito", value: "total_credit_amount", align: "end" },
      { text: "Total Cobrado", value: "total_paid_amount", align: "end" },
      { text: "Balance pendiente", value: "total_pending_amount", align: "end" },
      { text: "Acciones", value: "actions", sortable: false, align: "center" },
    ],
    transactions: [],
    transactionsPage: 1,
    transactionsLimit: 15,
    transactionsTotal: 0,
    transactionsTotalPages: 0,
    headersTransactions: [
      {
        text: "Número pago",
        align: "start",
        sortable: false,
        value: "invoice_payment_id",
      },
      { text: "Cliente", value: "entity_name" },
      { text: "Usuario", value: "payment_by_name" },
      { text: "Monto total", align: "end", value: "total_amount" },
      { text: "Contabilidad", value: "accounting", align: "center" },
      { text: "Estado", value: "status_id", align: "center" },
      { text: "Acciones", value: "actions", sortable: false, align: "center" },
    ],
    selectPayments: null,
    lastData: null,
    selectedClients: [],
    error: null,
    // Manual Filters Data
    itemsBussines: [],
    itemsLocations2: [],
    itemsLocations: [],
    menu_date_end: false,
    menu_date_start: false,
    date2: moment().format("YYYY-MM-DD"),
    dateStart: moment().subtract(90, 'days').format("YYYY-MM-DD"),
    useRangeFilter: false,
    aging_filter: null,
    aging_options: [
      { text: 'Todas', value: null },
      { text: '0-30 días', value: '0-30' },
      { text: '31-60 días', value: '31-60' },
      { text: '61-90 días', value: '61-90' },
      { text: 'Más de 90 días', value: '90+' },
      { text: 'Vencidas', value: 'overdue' },
      { text: 'Por vencer', value: 'upcoming' }
    ],
    currencies: [],
    seleted_currencies: null,
    allSystemCurrencies: [],
    dashboardLoading: false,
    _dashboardCalculating: false,
    dashboardCurrencyChanging: false,
    dashboardConvertTo: false,
    dashboardCurrencyTo: null,
    dashboardFilters: '',
    dashboardData: {
      kpis: {
        totalPending: 0,
        overdueCount: 0,
        avgDPO: 0,
        activeSuppliers: 0,
        overdueAmount: 0
      },
      aging: {
        range0_30: 0,
        range31_60: 0,
        range61_90: 0,
        range91_120: 0,
        range120plus: 0
      },
      status: {
        overdue: 0,
        upcoming: 0,
        onTime: 0
      },
      topSuppliers: [],
      agingStacked: [],
      paretoData: [],
      locations: [],
      agingTable: [],
      trends: [],
      dpo: {
        value: 0,
        trend: 'stable'
      },
      currencies: []
    },
  }),

  watch: {
    async typeForm() {
      if (this.typeForm === 2) {
        await this.update_filters();
        await this.calculateDashboardData();
      } else if (this.typeForm === 1) {
        // When switching to payments tab, default to current week range
        console.log('Switching to payments tab, setting current week range');
        this.useRangeFilter = true;
        const weekRange = this.getCurrentWeekRange();
        this.dateStart = weekRange.start;
        this.date2 = weekRange.end;
        console.log('Week range set:', { start: this.dateStart, end: this.date2, useRangeFilter: this.useRangeFilter });
        
        // Call update_filters once after setting dates
        await this.update_filters();
      } else {
        // For other tabs, just update filters normally
        await this.update_filters();
      }
    },
    async seleted_currencies() {
      if (this.dashboardCurrencyChanging) {
        this.dashboardCurrencyChanging = false;
        return;
      }
    },
  },
  created() {
    this.itemsBussines = [...this.$store.state.auth.bussines_units];
   // this.itemsBussines.unshift({ text: "Todas", value: 0 });
    this.itemsLocations = [...(this.$store.state.auth.locations || [])];
    this.updateLocationsForBusiness();
    this.update_filters();
  },
  computed: {
    business_selected: {
      get() {
        return this.$store.state.app.filters.business || this.$store.state.auth.bussines_units[0]?.value;
      },
      set(value) {
        this.$store.commit("setBusiness", value);
      },
    },
    location_selected: {
      get() {
        return this.$store.state.app.filters.location;
      },
      set(value) {
        if (this.isValidLocationValue(value)) {
          this.$store.commit("setLocation", value);
        }
      },
    },
    currentBusinessUnitName() {
      const bu = this.itemsBussines.find(b => b.value === this.business_selected);
      return bu ? bu.text : 'Todas las Unidades';
    },
    currentCurrencyCode() {
      const cur = this.currencies.find(c => c.id === this.seleted_currencies);
      return cur ? cur.code : 'USD';
    },
    currentLocationName() {
      if (this.location_selected && typeof this.location_selected === 'object' && this.location_selected.value && this.location_selected.value !== 0) {
        return this.location_selected.text || '';
      }
      return '';
    },
    date2FormatDMY() {
      return moment(this.date2).format("DD/MM/YYYY");
    },
    dateStartFormatDMY() {
      return moment(this.dateStart).format("DD/MM/YYYY");
    },
    today() {
      const m = moment();
      return {
        day: m.format("DD"),
        month: m.format("MMMM").toUpperCase(),
        year: m.format("YYYY"),
      };
    },
    // Group amounts by currency
    statsByCurrency() {
      const currencyGroups = {};

      this.clients.forEach((client) => {
        client.details.forEach((currencyTotal) => {
          const currencyId = currencyTotal.currency_id;

          if (!currencyGroups[currencyId]) {
            currencyGroups[currencyId] = {
              invoice_quantity: 0,
              pending_amount: 0,
              total_credits: 0,
              total_debits: 0,
              total_paid: 0,
              currency_code: currencyTotal.currency_code,
            };
          }

          // Usando las propiedades de currencyTotal en lugar de client
          currencyGroups[currencyId].invoice_quantity +=
            parseInt(currencyTotal.invoice_count) || 0;
          currencyGroups[currencyId].pending_amount +=
            parseFloat(currencyTotal.pending_amount) || 0;
          currencyGroups[currencyId].total_credits +=
            parseFloat(currencyTotal.total_credits) || 0;
          currencyGroups[currencyId].total_paid +=
            parseFloat(currencyTotal.total_paid) || 0;
          currencyGroups[currencyId].total_debits +=
            parseFloat(currencyTotal.total_debits) || 0;
        });
      });

      return currencyGroups;
    },

    // Consolidated stats for display
    consolidatedStats() {
      const currencyGroups = this.statsByCurrency;

      return [
        {
          title: "Notas de crédito",
          icon: "mdi-receipt-text-minus-outline",
          tone: "warning",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.total_credits,
            currencyCode: group.currency_code,
          })),
          color: "warning",
          isSingleValue: false,
        },
        {
          title: "Notas de débito",
          icon: "mdi-receipt-text-plus-outline",
          tone: "info",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.total_debits,
            currencyCode: group.currency_code,
          })),
          color: "info",
          isSingleValue: false,
        },
        {
          title: "Cobrado",
          icon: "mdi-cash-check",
          tone: "success",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.total_paid,
            currencyCode: group.currency_code,
          })),
          color: "success",
          isSingleValue: false,
        },
        {
          title: "Por cobrar",
          icon: "mdi-wallet-outline",
          tone: "error",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.pending_amount,
            currencyCode: group.currency_code,
          })),
          color: "error",
          isSingleValue: false,
        },
      ];
    },
  },
  methods: {
    getCurrentWeekRange() {
      const today = moment();
      const startOfWeek = today.clone().startOf('week').format('YYYY-MM-DD'); // Sunday
      const endOfWeek = today.clone().endOf('week').format('YYYY-MM-DD'); // Saturday
      return { start: startOfWeek, end: endOfWeek };
    },
    isValidLocationValue(value) {
      if (value === null || value === undefined) return false;
      if (typeof value === "number" && value === 0) return true;
      if (typeof value === "object" && value.value !== undefined) return true;
      return typeof value === "number" && value > 0;
    },
    formatValue(stat) {
      if (!stat.isSingleValue) {
        return stat.values
          .map((v) => this.currencyFormat(v.amount, v.currencyCode))
          .join("\n");
      }

      if (stat.currencyCode) {
        return this.currencyFormat(stat.value, stat.currencyCode);
      }

      return stat.value.toLocaleString();
    },
    getCurrencyCode(currencyId) {
      // Aquí deberías mapear el currency_id al código correspondiente
      const currencyMap = {
        1: "DOP",
        2: "HTG",
        3: "USD",
        // Agregar más según necesites
      };
      return currencyMap[currencyId] || "USD";
    },
    async getData(data) {
      this.lastFilters = data;

      this.loading = true;
      this.error = null;
      this.clients = [];
      this.hasData = false;

      try {
        if (!data || !data.business) {
          return;
        }

        this.filters = {
          ...data,
          aging_filter: this.aging_filter,
          useRangeFilter: this.useRangeFilter
        };
        let filter = "and INVOICE_RESUMES.invoice_type_id IN (2,3) ";
        filter += data.business
          ? ` AND INVOICE_RESUMES.business_unit_id=${data.business}`
          : "";
        filter +=
          data.location && data.location !== null
            ? ` AND INVOICE_RESUMES.location_id=${data.location}`
            : "";

        // Filtro de antigüedad tiene prioridad sobre el rango de fechas
        if (this.aging_filter) {
          const today = moment().format('YYYY-MM-DD');
          switch (this.aging_filter) {
            case '0-30':
              filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE >= '${moment().subtract(30, 'days').format('YYYY-MM-DD')}'`;
              break;
            case '31-60':
              filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE BETWEEN '${moment().subtract(60, 'days').format('YYYY-MM-DD')}' AND '${moment().subtract(31, 'days').format('YYYY-MM-DD')}'`;
              break;
            case '61-90':
              filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE BETWEEN '${moment().subtract(90, 'days').format('YYYY-MM-DD')}' AND '${moment().subtract(61, 'days').format('YYYY-MM-DD')}'`;
              break;
            case '90+':
              filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE < '${moment().subtract(90, 'days').format('YYYY-MM-DD')}'`;
              break;
            case 'overdue':
              filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE < '${today}'`;
              break;
            case 'upcoming':
              filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE >= '${today}'`;
              break;
          }
        } else {
          // Si no hay filtro de antigüedad, usar el rango de fechas
          if (this.useRangeFilter && data.dateStart && data.dateEnd) {
            filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE BETWEEN '${data.dateStart}' AND '${data.dateEnd}' `;
          } else if (data.dateEnd) {
            filter += ` AND INVOICE_RESUMES.EXPIRE_AT::DATE <= '${data.dateEnd}' `;
          }
        }

        let resultclients = await this.api_Post(
          "transactions/customers_pending_balances",
          { filters: filter }
        );

        if (resultclients.status === 200) {
          this.clients = await resultclients.json();
          this.hasData = this.clients.length > 0;
        } else {
          throw new Error(`API responded with status ${resultclients.status}`);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        this.error = "Error al cargar los clientes. Por favor, intente de nuevo.";
        this.$toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    async getPayments(data, page = 1) {
      this.loading = true;
      this.transactions = [];

      try {
        console.log('getPayments called with data:', data);
        console.log('useRangeFilter:', this.useRangeFilter);
        console.log('dateStart:', this.dateStart, 'date2:', this.date2);
        
        if (!data || !data.business) {
          return;
        }

        let filter = "WHERE invoice_type_id IN (2,3) ";
        // Antes solo miraba el primer detalle (->0), lo que dejaba fuera pagos
        // cuya factura cat-2 no estaba en la posicion 0. Ahora revisa TODOS los detalles.
        filter += ` AND EXISTS (SELECT 1 FROM json_array_elements(payment_details::json) AS d WHERE d->>'invoice_category_id' = '2')`;
        filter += data.business ? ` AND business_unit_id=${data.business}` : "";
        filter += data.location && data.location !== 0 ? ` AND location_id=${data.location}` : "";

        // FORCE: Always use date range when on payments tab (typeForm === 1)
        if (this.typeForm === 1) {
          // Ensure we have current week range
          if (!this.dateStart || !this.date2) {
            const weekRange = this.getCurrentWeekRange();
            this.dateStart = weekRange.start;
            this.date2 = weekRange.end;
            console.log('Forced current week range:', { start: this.dateStart, end: this.date2 });
          }
          filter += ` AND payment_date::DATE BETWEEN '${this.dateStart}' AND '${this.date2}'`;
          console.log('FORCED: Using date range:', this.dateStart, 'to', this.date2);
        } else if (this.useRangeFilter && data.dateStart && data.dateEnd) {
          filter += ` AND payment_date::DATE BETWEEN '${data.dateStart}' AND '${data.dateEnd}'`;
          console.log('Using date range:', data.dateStart, 'to', data.dateEnd);
        } else if (data.dateEnd) {
          filter += ` AND payment_date::DATE <= '${data.dateEnd}'`;
          console.log('Using single date end:', data.dateEnd);
        }
        this.lastPaymentsFilter = filter;
        let resultpayments=null;

        if(this.typeForm===1){//LISTA DE COBROS SIN LA VISTA, MAS RAPIDA, SOLO FUNCIONA PARA CXC YA QUE UN GRUPO DE PAGO SOLO CONTIENE 1 CLIENTE
                 resultpayments = await this.api_Post("transactions/view_deposits_cxc", {
                   business_unit_id:data.business,
                    location_id:data.location,
                    date_start:data.dateStart,
                    date_end:data.dateEnd,
                    page: page,
                    limit: this.transactionsLimit
              });
        }else{
           resultpayments = await this.api_Post("transactions/viewAllPayemnts_paginated", {
            filters: filter,
            page: page,
            limit: this.transactionsLimit
          });
        }
        if (resultpayments.status === 200) {
          const response = await resultpayments.json();
          this.transactions = response.data;
          console.log("data",this.transactions)
          this.transactionsTotal = response.total;
          this.transactionsTotalPages = response.totalPages;
          this.transactionsPage = response.page;
        } else {
          throw new Error(`API responded with status ${resultpayments.status}`);
        }
      } catch (error) {
        console.error("Error fetching payments:", error);
        this.error = "Error al cargar los pagos. Por favor, intente de nuevo.";
        this.$toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    onTransactionsPageChange(page) {
      this.getPayments(this.lastData, page);
    },
    openCancelPaymentDialog(item) {
      this.selectPayments = item;
      this.dialogCancelPayment = true;
    },
    async cancelPayment() {
      try {
        let result = await this.api_Post("transactions/cancel_payment_full", {
          payment_id: this.selectPayments.invoice_payment_id,
        });
        if (result.status === 200) {
          this.$toast.success("Pago anulado y descontabilizado exitosamente");
          this.dialogCancelPayment = false;
          await this.getPayments(this.lastData);
        } else {
          throw new Error(`API responded with status ${result.status}`);
        }
      } catch (error) {
        console.error("Error cancelling payment:", error);
        this.$toast.error("Error al anular el pago. Por favor, intente de nuevo.");
      }
    },
    async reset() {
      this.dialog = false;
      this.loaded = true;
      await this.changeBusiness(this.lastData);
      this.loaded = false;
    },
    groupByEntityId(array) {
      if (array === null) return [];
      const grouped = {};
      array.forEach((item) => {
        const entityId = item.entity_id;
        if (!grouped[entityId]) {
          grouped[entityId] = [];
        }
        grouped[entityId].push(item);
      });
      return grouped;
    },
    colorRandom() {
      let r = Math.floor(Math.random() * 256);
      let g = Math.floor(Math.random() * 256);
      let b = Math.floor(Math.random() * 256);
      return (
        "#" +
        r.toString(16).padStart(2, "0") +
        g.toString(16).padStart(2, "0") +
        b.toString(16).padStart(2, "0")
      );
    },
    async changeBusiness(data) {
      this.lastData = data;
      this.lastFilters = data;
      this.hasData = true;

      const loaders = [
        () => this.getData(data),
        () => this.getPayments(data)
      ];

      if (loaders[this.typeForm]) {
        await loaders[this.typeForm]();
      }

      if (this.typeForm === 2) {
        await this.calculateDashboardData();
      }

      const resultCurrencies = await this.api_Post("accounting/currenciesBusiness", {
        business: data.business,
        effective_date: new Date(),
      });
      this.currencies = await resultCurrencies.json();
      this.allSystemCurrencies = this.currencies;
      if (!this.seleted_currencies && this.currencies.length > 0) {
        this.seleted_currencies = this.currencies[0].id;
      }
    },
    update_filters() {
      this.changeBusiness({
        business: this.business_selected,
        location: this.location_selected?.value || 0,
        dateStart: this.dateStart,
        dateEnd: this.date2,
        aging_filter: this.aging_filter,
      });
    },
    toggleRangeFilter() {
      this.useRangeFilter = !this.useRangeFilter;
      if (!this.useRangeFilter) {
        this.dateStart = moment().subtract(90, 'days').format("YYYY-MM-DD");
      } else if (this.typeForm === 1) {
        // When activating range filter on payments tab, use current week
        const weekRange = this.getCurrentWeekRange();
        this.dateStart = weekRange.start;
        this.date2 = weekRange.end;
      }
      this.update_filters();
    },
    updateLocationsForBusiness() {
      if (!this.$store.state.auth.locations || this.$store.state.auth.locations.length === 0) {
        setTimeout(() => {
          if (this.$store.state.auth.locations && this.$store.state.auth.locations.length > 0) {
            this.updateLocationsForBusiness();
          }
        }, 500);
        this.update_filters();
        return;
      }

      this.itemsLocations2 = [];

      if (this.business_selected && this.business_selected != 0) {
        this.itemsLocations2 = this.updateCombo(
          this.itemsLocations,
          this.itemsLocations2,
          this.business_selected,
          "location"
        );
      } else {
        this.itemsLocations2 = [...this.itemsLocations];
        this.itemsLocations2.unshift({ text: "Todas", value: 0 });
      }
    },
    updateCombo(itemsOrigen, itemsDestino, condicion, combo) {
      if (!itemsOrigen || !Array.isArray(itemsOrigen) || itemsOrigen.length === 0) {
        itemsDestino.splice(0, itemsDestino.length);
        itemsDestino.unshift({ text: "Todas", value: 0 });
        return itemsDestino;
      }

      itemsDestino.splice(0, itemsDestino.length);

      for (let item of itemsOrigen) {
        if (item.bus_id === condicion) {
          itemsDestino.push({
            value: item.value,
            text: item.text,
            routes: item.routes,
          });
        }
      }
      itemsDestino.unshift({ text: "Todas", value: 0 });

      // Validar selección actual
      const hasValidSelection =
        this.location_selected &&
        (typeof this.location_selected === "object" ||
          (typeof this.location_selected === "number" && this.location_selected !== 0));

      if (!hasValidSelection) {
        this.location_selected = itemsDestino[0];
      } else {
        const currentSelectionExists = itemsDestino.find((item) => {
          if (typeof this.location_selected === "object") {
            return item.value === this.location_selected.value;
          } else {
            return item.value === this.location_selected;
          }
        });

        if (!currentSelectionExists && this.location_selected.value !== 0) {
        }
      }

      return itemsDestino;
    },
    async viewInvoices(item) {
      this.loaded = true;
      this.loadingInvoices = true;
      try {
        let data = {
          entity_id: item.entity_id,
          business: this.lastFilters.business,
          location: this.lastFilters.location,
        };

        // Solo pasar fechas si no hay filtro de antigüedad activo
        if (!this.aging_filter) {
          if (this.useRangeFilter) {
            data.start_date = this.lastFilters.dateStart;
            data.end_date = this.lastFilters.dateEnd;
          } else {
            data.end_date = this.lastFilters.dateEnd;
          }
        }
        // Si hay filtro de antigüedad, no pasar fechas para que el backend no las aplique

        let responseRequest = await this.api_Post("transactions/invoice_customers", data);
        if (responseRequest.status === 200) {
          this.selectedClients = await responseRequest.json();
          this.dialog = true;
        } else {
          throw new Error(`API responded with status ${responseRequest.status}`);
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
        this.$toast.error("Error al cargar las facturas. Por favor, intente de nuevo.");
      } finally {
        this.loaded = false;
        this.loadingInvoices = false;
      }
    },
    refreshInvoices() {
      this.getData(this.lastData);
    },

    filLeft(value, length, char) {
      return value.toString().padStart(length, char);
    },
    viewEntity(id, isCustomer) {
      let route = this.$router.resolve({
        name: "ViewEntitys",
        params: { id: id, isCustomer: isCustomer },
      });
      window.open(route.href);
    },
    async export_payment_details() {
      if (!this.lastPaymentsFilter) {
        this.$toast.warning('Aplica los filtros antes de exportar');
        return;
      }
      this.loading_export = true;
      const resultExport = await this.api_Post('transactions/viewAllPayemnts', {
        filters: this.lastPaymentsFilter,
      });
      const allData = await resultExport.json();
      this.loading_export = false;

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Detalle de Facturas')

      // =========================
      // Columnas del Excel
      // =========================
      worksheet.columns = [
        // ---- Grupo de pago ----
        { header: 'ID Pago', key: 'invoice_payment_id', width: 12 },
        { header: 'Asiento', key: 'diary_book_code', width: 12 },
        { header: 'Fecha Efectiva Pago', key: 'payment_date', width: 15 },
        { header: 'Fecha Contable pago', key: 'account_date', width: 15 },
        { header: 'Pagado Por', key: 'payment_by_name', width: 30 },
        { header: 'Tipo de Pago', key: 'payment_type_name', width: 18 },
        { header: 'Cuenta Bancaria', key: 'account_name', width: 25 },
        { header: 'Banco', key: 'bank_account_description_long', width: 20 },
        { header: 'Moneda transaccion', key: 'transaction_currency_code', width: 10 },
        { header: 'Monto transaccion', key: 'transaction_payment_amount', width: 16 },
        { header: 'Moneda base', key: 'base_currency_code', width: 10 },
        { header: 'Tasa moneda base', key: 'base_currency_rate', width: 16 },
        { header: 'Monto moneda base', key: 'base_amount', width: 16 },
        { header: 'Estado Pago', key: 'payment_status', width: 12 },
        { header: 'Contabilización', key: 'recorded_status', width: 18 },

        // ---- Factura ----
        { header: 'Empresa', key: 'entity_name', width: 25 },
        { header: 'RNC / Documento', key: 'entity_document_id', width: 18 },
        { header: 'Unidad de Negocio', key: 'business_unit_description', width: 22 },
        { header: 'Localidad', key: 'location_description', width: 22 },
        { header: 'Tipo Factura', key: 'invoice_type_name', width: 18 },
        { header: 'No. Factura', key: 'invoice_number', width: 20 },
        { header: 'NCF', key: 'invoice_ncf', width: 18 },
        { header: 'Fecha Emisión factura', key: 'emission_date', width: 15 },
        { header: 'Fecha Vencimiento factura', key: 'expire_at', width: 18 },
        { header: 'Moneda factura', key: 'invoice_currency_code', width: 14 },
        { header: 'Subtotal', key: 'subtotal_amount', width: 14 },
        { header: 'Impuestos', key: 'taxes_amount', width: 14 },
        { header: 'Total Factura', key: 'total_amount', width: 16 },
        { header: 'Nota', key: 'note', width: 35 }
      ]

      // =========================
      // Data
      // =========================
      allData.forEach(group => {
        // const paymentStatus = group.status_id === 1 ? 'Activo' : 'Inactivo'
        const recordedStatus = group.recorded ? 'Contabilizado' : 'Descontabilizado'

        group.payment_details.forEach(detail => {
          // CxC: factura de venta (2) y documento interno por cobrar (3)
          if (group.status_id != 4 && [2, 3].includes(detail.invoice_type_id)) {

            worksheet.addRow({
              // ---- Grupo de pago ----
              invoice_payment_id: group.invoice_payment_id,
              diary_book_code: group.diary_book_code,
              payment_date: group.payment_date,
              account_date: group.account_date,
              payment_by_name: group.payment_by_name,
              payment_type_name: group.payment_type_name,
              account_name: group.account_name,
              bank_account_description_long: group.bank_account_description_long,
              transaction_currency_code: group.currency_code,
              transaction_payment_amount: detail.transaction_amount,
              base_currency_code: detail.business_currency_code,
              base_currency_rate: 1 / parseFloat(detail.business_unit_currency_rate),
              base_amount: parseFloat(detail.business_unit_currency_rate) * parseFloat(detail.transaction_amount),
              payment_status: group.status_name,
              recorded_status: recordedStatus,

              // ---- Factura ----
              entity_name: detail.entity_name,
              entity_document_id: detail.entity_document_id,
              business_unit_description: detail.business_unit_description,
              location_description: detail.location_description,
              invoice_type_name: detail.invoice_type_name,
              invoice_number: detail.invoice_number,
              invoice_ncf: detail.invoice_ncf,
              emission_date: detail.emission_date,
              expire_at: detail.expire_at,
              invoice_currency_code: detail.currency_code,
              subtotal_amount: detail.subtotal_amount,
              taxes_amount: detail.taxes_amount,
              total_amount: detail.total_amount,
              note: detail.note
            })
          }
        })
      })

      // =========================
      // Estilos básicos
      // =========================
      worksheet.getRow(1).font = { bold: true }
      worksheet.autoFilter = 'A1:Y1'

      // =========================
      // Exportar
      // =========================
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      //{{report_title.title}} {{report_title.is_default?'':report_title.union+report_title.origin+' '+report_title.date_format}}
      link.download = `reporte_detallado_facturas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
    },
    async onDashboardCurrencyChange(currencyId) {
      this.dashboardCurrencyChanging = true;
      this.seleted_currencies = currencyId;
      await this.calculateDashboardData();
    },
    async onDashboardConvertChange({ convert_to, currency_to }) {
      this.dashboardConvertTo = convert_to;
      this.dashboardCurrencyTo = currency_to;
      await this.calculateDashboardData();
    },
    async calculateDashboardData() {
      if (!this.lastData) return;
      if (this._dashboardCalculating) return;
      this._dashboardCalculating = true;

      this.dashboardLoading = true;
      try {
        let filter = "AND invoice_type_id IN (2,3) ";
        filter += this.lastData.business == 0 ? "" : " AND business_unit_id=" + this.lastData.business;
        filter += this.lastData.location && this.lastData.location !== null ? ` AND location_id=${this.lastData.location}` : "";

        if (this.useRangeFilter && this.lastData.dateStart && this.lastData.dateEnd) {
          filter += ` AND EXPIRE_AT::DATE BETWEEN '${this.lastData.dateStart}' AND '${this.lastData.dateEnd}'`;
        } else if (this.lastData.dateEnd) {
          filter += ` AND EXPIRE_AT::DATE <= '${this.lastData.dateEnd}'`;
        }

        if (!this.dashboardConvertTo) {
          filter += this.seleted_currencies == null ? "" : " AND currency_id=" + this.seleted_currencies;
        }
        filter += ` AND LOCATION_ID IN (${this.$store.state.auth.locations.map(item => item.id).join(',')})`;

        // Apply aging filter if selected
        if (this.aging_filter) {
          const today = moment().format('YYYY-MM-DD');
          switch (this.aging_filter) {
            case '0-30':
              filter += ` AND EXPIRE_AT::DATE >= '${moment().subtract(30, 'days').format('YYYY-MM-DD')}'`;
              break;
            case '31-60':
              filter += ` AND EXPIRE_AT::DATE BETWEEN '${moment().subtract(60, 'days').format('YYYY-MM-DD')}' AND '${moment().subtract(31, 'days').format('YYYY-MM-DD')}'`;
              break;
            case '61-90':
              filter += ` AND EXPIRE_AT::DATE BETWEEN '${moment().subtract(90, 'days').format('YYYY-MM-DD')}' AND '${moment().subtract(61, 'days').format('YYYY-MM-DD')}'`;
              break;
            case '90+':
              filter += ` AND EXPIRE_AT::DATE < '${moment().subtract(90, 'days').format('YYYY-MM-DD')}'`;
              break;
            case 'overdue':
              filter += ` AND EXPIRE_AT::DATE < '${today}'`;
              break;
            case 'upcoming':
              filter += ` AND EXPIRE_AT::DATE >= '${today}'`;
              break;
          }
        }

        this.dashboardFilters = filter;

        const response = await this.api_Post("transactions/invoice_sales_dashboard", {
          filters: filter,
          convert_to: this.dashboardConvertTo,
          currency_to: this.dashboardConvertTo ? this.dashboardCurrencyTo : null,
          business: this.lastData.business
        });

        if (response.status === 200) {
          const data = await response.json();

          // Update KPIs
          this.dashboardData.kpis = {
            totalPending: parseFloat(data.total_pending || 0),
            overdueCount: parseInt(data.overdue_count || 0),
            avgDPO: Math.round(parseFloat(data.avg_dpo || 0)),
            activeSuppliers: parseInt(data.active_suppliers || 0),
            overdueAmount: parseFloat(data.overdue_amount || 0)
          };

          // Update Aging
          this.dashboardData.aging = {
            range0_30: parseFloat(data.aging_0_30 || 0),
            range31_60: parseFloat(data.aging_31_60 || 0),
            range61_90: parseFloat(data.aging_61_90 || 0),
            range91_120: parseFloat(data.aging_91_120 || 0),
            range120plus: parseFloat(data.aging_120plus || 0)
          };

          // Update Status
          this.dashboardData.status = {
            overdue: parseInt(data.status_overdue || 0),
            upcoming: parseInt(data.status_upcoming || 0),
            onTime: parseInt(data.status_on_time || 0)
          };

          // Update Top Suppliers
          this.dashboardData.topSuppliers = data.top_suppliers || [];

          // Update Stacked Aging + Pareto + Locations
          this.dashboardData.agingStacked = data.aging_stacked || [];
          this.dashboardData.paretoData = data.pareto_data || [];
          this.dashboardData.locations = data.locations || [];
          this.dashboardData.agingTable = data.aging_table || [];

          // Update Currencies
          this.dashboardData.currencies = data.currencies || [];

          // Update DPO
          this.dashboardData.dpo.value = this.dashboardData.kpis.avgDPO;

          // Force reactivity - reassign entire object so child watcher triggers with all data
          this.dashboardData = { ...this.dashboardData };
        }
      } catch (error) {
        console.error("Error al calcular datos del dashboard:", error);
      } finally {
        this.dashboardLoading = false;
        this._dashboardCalculating = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
// ── Palette aligned with the Vuetify theme (teal primary, MD tokens) ──
$ink:        #2A3547;
$muted:      #5A6A85;
$muted-soft: #8A98AB;
$rule:       #E5EAF2;
$bg:         #F7F9FC;
$card:       #FFFFFF;

$primary:      #00897B;
$primary-soft: #E0F2F1;
$info:         #2196F3;
$info-soft:    #E3F2FD;
$warning:      #F57F17;
$warning-soft: #FFF3E0;
$success:      #2E7D32;
$success-soft: #E8F5E9;
$error:        #E53935;
$error-soft:   #FFEBEE;

$shadow:    rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 8px 18px -6px;
$shadow-lg: rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 16%) 0px 14px 28px -6px;
$font: 'Plus Jakarta Sans', sans-serif;

.fc-cxc {
  font-family: $font;
  color: $ink;
  background: $bg;
  min-height: 100vh;
  padding: clamp(18px, 2.4vw, 32px) clamp(16px, 3vw, 40px) 56px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// ── HERO ─────────────────────────────────────────────────────
.fc-hero {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  padding: clamp(22px, 2.6vw, 34px);
  border-radius: 16px;
  background: linear-gradient(120deg, #ffffff 0%, #f3faf8 100%);
  border: 1px solid $rule;
  box-shadow: $shadow;
  overflow: hidden;
}
.fc-hero::before {
  content: "";
  position: absolute;
  inset: 0 0 0 auto;
  width: 5px;
  background: linear-gradient(180deg, $primary, lighten($primary, 18%));
}
.fc-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 10px;
}
.fc-hero__title {
  font-family: $font;
  font-size: clamp(26px, 3.2vw, 36px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -.01em;
  color: $ink;
  margin: 0 0 10px;
}
.fc-hero__lede {
  font-size: 14px;
  line-height: 1.55;
  color: $muted;
  max-width: 60ch;
  margin: 0;
}
.fc-hero__lede strong { color: $ink; font-weight: 700; }

.fc-hero__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.fc-hero__date { display: flex; align-items: center; gap: 12px; }
.fc-hero__date-day {
  font-size: 52px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -.03em;
  color: $primary;
}
.fc-hero__date-rest {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}
.fc-hero__date-month { font-size: 13px; font-weight: 700; letter-spacing: .14em; color: $ink; }
.fc-hero__date-year  { font-size: 13px; font-weight: 600; color: $muted; }

.fc-chip-live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: $primary;
  padding: 6px 12px;
  border-radius: 999px;
  background: $primary-soft;
}
.fc-chip-live__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $primary;
  box-shadow: 0 0 0 3px rgba(0, 137, 123, .18);
  animation: fc-pulse 1.8s ease-in-out infinite;
}
@keyframes fc-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(0, 137, 123, .18); }
  50%      { box-shadow: 0 0 0 8px rgba(0, 137, 123, 0); }
}

// ── SECTION ──────────────────────────────────────────────────
.fc-section { margin: 0; }
.fc-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.fc-section__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -.01em;
  color: $ink;
  margin: 0;
}
.fc-section__status { font-size: 12px; font-weight: 600; color: $muted; margin-left: 4px; }

// ── CARD ─────────────────────────────────────────────────────
.fc-card {
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
}
.fc-card--filters { padding: 14px clamp(12px, 1.4vw, 18px) 6px; }
.fc-card--board,
.fc-card--table { overflow: hidden; }

// ── KPI ──────────────────────────────────────────────────────
.fc-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 18px;
}
.fc-kpi {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 20px 18px;
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
  overflow: hidden;
  transition: transform .2s ease, box-shadow .2s ease;
}
.fc-kpi:hover { transform: translateY(-3px); box-shadow: $shadow-lg; }
.fc-kpi__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.fc-kpi__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
}
.fc-kpi--warning .fc-kpi__icon { background: $warning-soft; }
.fc-kpi--info    .fc-kpi__icon { background: $info-soft; }
.fc-kpi--success .fc-kpi__icon { background: $success-soft; }
.fc-kpi--error   .fc-kpi__icon { background: $error-soft; }
.fc-kpi__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: $muted;
}
.fc-kpi__values { display: flex; flex-direction: column; gap: 2px; margin-top: 2px; }
.fc-kpi__money { display: flex; align-items: baseline; gap: 6px; }
.fc-kpi__number {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -.02em;
  color: $ink;
}
.fc-kpi__currency { font-size: 12px; font-weight: 700; color: $muted-soft; text-transform: uppercase; }

// ── TABS / BOARD ─────────────────────────────────────────────
.fc-tabs { padding: 0 8px; }
.fc-tab {
  font-family: $font;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.fc-board__pane { padding: clamp(14px, 1.8vw, 24px); }
.fc-panes { background: transparent; overflow: visible; }

// ── TABLE HEAD ───────────────────────────────────────────────
.fc-table__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px clamp(14px, 1.6vw, 20px);
  border-bottom: 1px solid $rule;
}
.fc-table__title {
  display: inline-flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: $ink;
}

@media (max-width: 720px) {
  .fc-hero { grid-template-columns: 1fr; }
  .fc-hero__right { flex-direction: row; align-items: center; justify-content: space-between; }
  .fc-kpis { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 460px) {
  .fc-kpis { grid-template-columns: 1fr; }
}

// ── Vuetify deep overrides ───────────────────────────────────
::v-deep .fc-card--filters,
::v-deep .fc-table__head,
::v-deep .fc-board__pane {
  .v-input__slot { background: #F7F9FC !important; min-height: 42px !important; }
  .v-input fieldset { border-color: $rule !important; border-radius: 8px !important; }
  .v-input--is-focused fieldset { border-color: $primary !important; }
  .v-label { font-family: $font !important; color: $muted !important; }
  .v-select__selection,
  .v-text-field input { font-family: $font !important; font-size: 14px !important; color: $ink !important; }
}

::v-deep .fc-data-table {
  background: transparent;

  .v-data-table-header th {
    background: #F7F9FC;
    color: $muted !important;
    font-weight: 700 !important;
    font-size: 11px !important;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  td { font-family: $font; color: $ink; }
  td, th { border-bottom: 1px solid $rule !important; }
  tbody tr { transition: background .18s ease; }
  tbody tr:hover { background: $primary-soft !important; }
  .v-data-footer { border-top: 1px solid $rule; font-family: $font; }
}

::v-deep .fc-tabs .v-tabs-items,
::v-deep .v-window__container { overflow: visible !important; }
</style>
